// Shared building blocks for the 11 view components in src/views/.
//
// Every view is a composition of widgets that fetches its data via the
// useTool hook and falls back to clean loading / error / empty states.
// Widget composition per UI_SPEC §3.11.

import { type ReactNode } from "react";
import { ArtifactView } from "../components/ArtifactView.js";
import type { Envelope } from "../lib/envelope.js";
import { useTool } from "../lib/useTool.js";
import { useShell } from "../components/ShellContext.js";

export function ViewHeader({ title, subtitle, actions }: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-end justify-between">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ title, children, className = "", actions }: {
  title?: string;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  return (
    <section className={`flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white ${className}`}>
      {(title || actions) && (
        <header className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          {title && <div className="font-medium text-slate-700">{title}</div>}
          {actions}
        </header>
      )}
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </section>
  );
}

export function Skeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-3 rounded bg-slate-100" style={{ width: `${100 - i * 8}%` }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="p-4">
      <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        <div className="font-medium">Could not load</div>
        <div className="mt-1 break-words font-mono text-xs">{error}</div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded border border-rose-300 px-2 py-0.5 text-xs text-rose-700 hover:bg-rose-100"
          >Retry</button>
        )}
      </div>
    </div>
  );
}

export function EmptyState({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="p-6 text-center">
      <div className="text-sm text-slate-500">{message}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

interface FetchedSlotProps {
  tool: string;
  args?: Record<string, unknown>;
  // Override rendering — by default we route the envelope through ArtifactView.
  // Pass `render` to draw the payload yourself (e.g. when the view composes
  // multiple typed widgets from one envelope's `data`).
  render?: (env: Envelope, refetch: () => Promise<void>) => ReactNode;
}

/** A self-fetching slot. Calls `useTool(tool, args)` and renders. */
export function FetchedSlot({ tool, args, render }: FetchedSlotProps) {
  const { project } = useShell();
  const fullArgs = { project, ...(args ?? {}) };
  const { env, error, loading, refetch } = useTool(tool, fullArgs);
  if (loading) return <Skeleton />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!env) return <EmptyState message="no data" />;
  if (render) return <>{render(env, refetch)}</>;
  return <ArtifactView envelope={env} onPairClick={(t) => {/* no-op in standalone view */ void t;}} />;
}
